import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'

import {
    IBooking,
    ILocation,
    ILocationPayloadMultilang,
    TBookingService,
    TEmployee,
    TTimeSlots,
    TWorkingDay,
} from './store/types'
import { minBookingServiceDurationMinutes } from './bookingApiHelpers'
import { buildBookingServices } from './bookingServiceHelpers'
import { generateTimeSlots } from './service/booking'

export const mapLocationToApiBody = (location: ILocation) => ({
    name: location.name,
    address: location.address,
    description: location.description ?? '',
    imageUrl: location.photoUrl ?? '',
})

export const mapLocationPayloadMultilang = (
    payload: ILocationPayloadMultilang,
) => ({
    en: payload.en
        ? {
              name: payload.en.name,
              address: payload.en.address,
              description: payload.en.description ?? '',
              imageUrl: payload.en.photoUrl ?? '',
          }
        : undefined,
    uk: payload.uk
        ? {
              name: payload.uk.name,
              address: payload.uk.address,
              description: payload.uk.description ?? '',
              imageUrl: payload.uk.photoUrl ?? '',
          }
        : undefined,
})

export const mapBookingLocationForCreateBooking = (location: ILocation) => ({
    id: location.id,
    name: location.name,
    address: location.address,
    imageUrl: location.photoUrl ?? '',
    description: location.description ?? '',
})

export const normalizeBookingLocationFromApi = (
    item: ILocation & { imageUrl?: string },
): ILocation => ({
    ...item,
    photoUrl: item.photoUrl || item.imageUrl || '',
})

const generateTS = async (
    appId: string,
    timeSlots: TWorkingDay[],
    duration: number,
): Promise<AxiosResponse<TTimeSlots[]> | undefined> => {
    const response = await generateTimeSlots({ appId, timeSlots, duration })
    if (response.status === 200) {
        return response
    }
    return undefined
}

const hasDuplicateEmployees = (schedule: TEmployee[]) => {
    const employeeIds = schedule
        .map(row => row.employeeId)
        .filter((id): id is string => Boolean(id))
    return new Set(employeeIds).size !== employeeIds.length
}

export const generateSlotForItem = async (
    appId: string,
    item: IBooking,
    sameUserErr: string,
): Promise<IBooking | undefined> => {
    if (!item.schedule?.length) {
        return item
    }

    const slotDuration = minBookingServiceDurationMinutes(item.services)

    if (hasDuplicateEmployees(item.schedule)) {
        toast.error(sameUserErr)
        return undefined
    }

    const scheduleResult = await item.schedule.reduce<
        Promise<TEmployee[] | undefined>
    >(async (accPromise, scheduleRow, index) => {
        const acc = await accPromise
        if (!acc) {
            return undefined
        }
        if (scheduleRow.generatedTimeSlots) {
            return acc
        }
        const response = await generateTS(
            appId,
            scheduleRow.timeSlots,
            slotDuration,
        )
        const scheduleCopy = [...acc]
        scheduleCopy[index] = {
            ...scheduleCopy[index],
            generatedTimeSlots: response?.data,
        }
        return scheduleCopy
    }, Promise.resolve([...item.schedule]))

    if (!scheduleResult) {
        return undefined
    }

    return { ...item, schedule: scheduleResult }
}

export const buildBookingFromBlock = (
    location: ILocation,
    schedule: TEmployee[],
    language: string,
    services?: TBookingService[],
): IBooking => {
    const activeSchedule = schedule.filter(employee => employee.employeeId)
    return {
        location,
        schedule: activeSchedule,
        language,
        services: services ?? buildBookingServices(activeSchedule),
    }
}

export const isNewBookingLocationNeeded = (location: ILocation) =>
    Boolean(location.name && location.address && !location.id)

export const getLocationsBodyFromBookingLocation = (
    location: ILocation | undefined,
) => {
    if (!location) {
        return { name: '', address: '', description: '', photoUrl: '' }
    }
    return {
        name: location.name,
        address: location.address,
        description: location.description ?? '',
        photoUrl: location.photoUrl ?? '',
    }
}

export const saveBlocksSequentially = async <
    T extends { location: ILocation; schedule: TEmployee[] },
>(
    blocks: T[],
    saveBlock: (block: T) => Promise<'success' | 'error'>,
): Promise<'success' | 'error'> =>
    blocks.reduce<Promise<'success' | 'error'>>(
        (chain, block) =>
            chain.then(status => {
                if (status === 'error') {
                    return 'error'
                }
                return saveBlock(block)
            }),
        Promise.resolve('success'),
    )
